import { type FC, useMemo } from "react";
import { Loader2 } from "lucide-react";

import { PeopleSelector } from "@/shared/components/PeopleSelector";
import { FieldError } from "@/shared/components/ui/FieldError";
import { OptionCard } from "@/shared/components/ui/OptionCard";
import { RadioGroup } from "@/shared/components/ui/radio-group";
import { EstateRole } from "@/shared/constants/contactDetailOptions";
import { useAssignLegalRole } from "@/shared/hooks/useAssignLegalRole";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { formatContactName, isWillWitness } from "@/shared/utils/contactUtils";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

const AFFIDAVIT_KEY = "self_proving_affidavit";
const WITNESS_KEY = "witness_status";

export const SelfProvingWitnessScreen: FC<FlowCustomComponentProps> = ({
  answers,
  setAnswer,
  errors,
}) => {
  const { contacts, isLoading } = useUserContacts();
  const assignLegalRole = useAssignLegalRole();

  const witnesses = useMemo(() => contacts.filter(isWillWitness), [contacts]);

  const firstWitness = witnesses[0];
  const witnessName = firstWitness
    ? formatContactName(firstWitness.first_name, firstWitness.last_name)
    : null;

  const affidavitAnswer = answers[AFFIDAVIT_KEY] as string | undefined;
  const witnessAnswer = answers[WITNESS_KEY] as string | undefined;

  const handleAffidavitSelect = (value: string) => {
    setAnswer(AFFIDAVIT_KEY, value);
    if (value === "yes") {
      setAnswer(WITNESS_KEY, "");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="size-8 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <p className="font-sans text-xl font-semibold leading-[30px]">
          Does the back of the will have a page with a official seal or stamp from a Notary Public?
        </p>
        <p className="text-sm text-secondary tracking-[-0.28px]">
          This is called a Self-Proving Affidavit. It usually says "State of..." at the top and has
          signatures from both the witnesses and a notary.
        </p>
      </div>

      <RadioGroup
        name="self_proving_affidavit"
        value={affidavitAnswer ?? ""}
        onValueChange={handleAffidavitSelect}
        className="flex flex-col gap-3"
      >
        <OptionCard
          label="Yes, it has a notary seal"
          selected={affidavitAnswer === "yes"}
          onClick={() => handleAffidavitSelect("yes")}
          value="yes"
        />
        <OptionCard
          label="No, or I'm not sure"
          selected={affidavitAnswer === "no_or_unsure"}
          onClick={() => handleAffidavitSelect("no_or_unsure")}
          value="no_or_unsure"
        />
      </RadioGroup>
      <FieldError message={errors?.self_proving_affidavit} />

      {affidavitAnswer === "no_or_unsure" && (
        <>
          <div className="border-t border-primary" />

          <div className="flex flex-col gap-1">
            <p className="font-sans text-xl font-semibold leading-[30px]">
              Can you contact {witnessName ?? "the witness"} to sign a sworn court document?
            </p>
            <p className="text-sm text-secondary tracking-[-0.28px]">
              Because there isn't a notary seal on the will, the court needs to confirm the witness
              was present when the document was signed.
            </p>
          </div>

          <PeopleSelector
            mode="multi"
            label="Witnesses named in the will"
            placeholder="Choose the witnesses"
            value={witnesses.map((witness) => witness.id)}
            onChange={(ids) => assignLegalRole(EstateRole.WillWitness, isWillWitness, ids)}
          />

          <RadioGroup
            name="witness_status"
            value={witnessAnswer ?? ""}
            onValueChange={(v) => setAnswer(WITNESS_KEY, v)}
            className="flex flex-col gap-3"
          >
            <OptionCard
              label="Yes, I can reach them"
              selected={witnessAnswer === "can_sign"}
              onClick={() => setAnswer(WITNESS_KEY, "can_sign")}
              value="can_sign"
            />
            <OptionCard
              label="No (they passed away, moved, or cannot be found)"
              selected={witnessAnswer === "unavailable"}
              onClick={() => setAnswer(WITNESS_KEY, "unavailable")}
              value="unavailable"
            />
          </RadioGroup>
          <FieldError message={errors?.witness_status} />
        </>
      )}
    </div>
  );
};
